/**
 * Domain constants and pure derivations shared by web and server.
 *
 * Everything here is extracted verbatim from the prototype (docs/HRMS.jsx) so the
 * production UI renders identical strings, orders and tones. Nothing in this file
 * reads the clock, the database or the network — it is safe on both sides of the seam.
 */

export const COMPANY = {
  legal: "SEO Surgeon Private Limited",
  address: "143 First Floor, Mukharji Park, New Delhi - 110018",
} as const;

/** Effective authorization roles. The server is authoritative for these. */
export const EFFECTIVE_ROLES = ["member", "manager", "hr_admin", "super_admin"] as const;
export type EffectiveRole = (typeof EFFECTIVE_ROLES)[number];

/** Assignable access roles shown in Settings → Access & roles (prototype ROLES). */
export const ACCESS_ROLES = ["Employee", "Finance", "Manager", "HR Admin", "Super Admin"] as const;
export type AccessRole = (typeof ACCESS_ROLES)[number];

/**
 * Column set of the Settings role matrix.
 *
 * `Resources` is not in the prototype's MODULES and is here because
 * `canManageResources` and `canDownloadResources` gate a whole screen and every
 * download in it: a matrix that HR reads before assigning a role and that is
 * silent about the document library is a matrix with a hole in it.
 */
export const MODULES = [
  "Teammates", "Leave", "Reimbursements", "Reviews", "Reports", "Resources", "Settings",
] as const;

/**
 * What each access role can actually do, cell for cell against `capabilitiesFor`
 * in `apps/server/src/services/authz.ts`.
 *
 * This is *not* the prototype's ROLE_MATRIX any more. That table was written
 * before the capability map existed and six of its cells advertised access the
 * server refuses outright — Finance was described as having a company-wide view
 * of teammates and leave and a Settings "Config" power, none of which
 * `capabilitiesFor` grants it (decision D3: Finance is an access role, not an
 * authorization tier, and its one extra power is `canPayClaims`); Manager was
 * described as having team reports against `canViewReports: isAdmin || isFinance`;
 * and HR Admin's Settings read "Most" against a Super Admin's "Full" where the
 * capability map treats the two identically.
 *
 * `seedRolePermissions` writes these rows into `role_permissions`, which is what
 * the Settings screen renders — so this table is the document HR reads before
 * assigning somebody a role, and every cell in it has to be a promise the
 * authorization layer keeps. Change a capability in `capabilitiesFor` and the
 * matching cell here changes with it, or the screen starts lying again.
 *
 * The prototype keys its matrix rows by "Employees" while rendering MODULES[0] =
 * "Teammates"; production stores it keyed by the rendered module names so the
 * matrix and its header cannot drift apart.
 */
export const ROLE_MATRIX: Record<AccessRole, Record<(typeof MODULES)[number], string>> = {
  Employee: { Teammates: "Self only", Leave: "Own leave", Reimbursements: "Own claims", Reviews: "Own reviews", Reports: "None", Resources: "View", Settings: "None" },
  Finance: { Teammates: "Self only", Leave: "Own leave", Reimbursements: "Process & pay", Reviews: "Own reviews", Reports: "View & export", Resources: "View", Settings: "None" },
  Manager: { Teammates: "Team view", Leave: "Approve team", Reimbursements: "Approve team", Reviews: "Run team reviews", Reports: "None", Resources: "View", Settings: "None" },
  "HR Admin": { Teammates: "Full + exits", Leave: "Full", Reimbursements: "Full", Reviews: "Full", Reports: "Full", Resources: "Manage", Settings: "Full, except access roles" },
  "Super Admin": { Teammates: "Full + exits", Leave: "Full", Reimbursements: "Full", Reviews: "Full", Reports: "Full", Resources: "Manage", Settings: "Full" },
};

/**
 * @deprecated Prototype constant — do not use it for anything a person sees.
 *
 * Departments are rows in the `departments` table, and this list is not those
 * rows: four of these seven names match no employee, and the one department that
 * holds the CEO ("Leadership") is missing, so any select built from this array
 * offers dead options and hides real people. Read the live list instead —
 * `GET /api/v1/departments`, or `useDepartments()` in
 * `apps/web/src/hooks/useDepartments.ts`, which also carries each department's
 * current headcount and whether it may be picked when adding a teammate.
 *
 * Kept only so screens that have not been migrated yet still compile.
 */
export const DEPTS = ["HR", "SEO", "Tech", "Growth", "Brand", "Design", "Finance"] as const;

/**
 * The department filter name that also matches leadership *members*.
 *
 * Leadership is a membership on top of a department: a Director of Tech is in Tech
 * and on the leadership team. `employees.is_leadership` records the second fact,
 * and filtering by this name matches the Leadership department or the flag. The
 * server applies the same rule in SQL through `in_department()` (migration 0042).
 */
export const LEADERSHIP_DEPARTMENT = "Leadership";

/** Whether a person falls under a department filter — the client twin of `in_department()`. */
export function isInDepartment(
  person: { department: string; isLeadership?: boolean },
  filter: string,
): boolean {
  return person.department === filter || (filter === LEADERSHIP_DEPARTMENT && person.isLeadership === true);
}

export const LEVELS = ["ceo", "director", "head", "sr-manager", "manager", "sr-analyst", "analyst", "jr", "intern"] as const;
export type Level = (typeof LEVELS)[number];

export const LEVEL_LABEL: Record<Level, string> = {
  ceo: "CEO", director: "Director", head: "Head", "sr-manager": "Sr. Manager",
  manager: "Manager", "sr-analyst": "Sr. Analyst", analyst: "Analyst", jr: "Jr. Analyst", intern: "Intern",
};

export const EMPLOYEE_STATUSES = ["Active", "Probation", "Intern", "Exited"] as const;
export type EmployeeStatus = (typeof EMPLOYEE_STATUSES)[number];

export const EMPLOYMENT_TYPES = ["Full-time", "Intern"] as const;
export type EmploymentType = (typeof EMPLOYMENT_TYPES)[number];

/** Prototype DOC_TYPES order. The n/N denominator comes from the DB, never this array. */
export const SEED_DOCUMENT_TYPES = [
  "Aadhaar card", "PAN card", "Cancelled cheque", "Bank account proof",
  "Address proof", "Education certificate", "Signed offer letter", "Passport photo",
] as const;

export const LEAVE_STATUSES = ["pending", "approved", "declined", "cancelled"] as const;
export type LeaveStatus = (typeof LEAVE_STATUSES)[number];

export const LEAVE_DURATIONS = ["Full day", "Morning half", "Afternoon half"] as const;
export type LeaveDuration = (typeof LEAVE_DURATIONS)[number];

/**
 * The three kinds a holiday is published under — the `holidays.kind` CHECK, in
 * one place so the admin form, the API and the column cannot drift apart.
 *
 * `National` and `Common` are days the office is shut, so they drop out of the
 * working-day count. `Restricted` is not: each person picks two of them and books
 * them through the Restricted Holiday leave type, so those dates stay working
 * days — treating them otherwise would make that leave type un-bookable.
 */
export const HOLIDAY_KINDS = ["National", "Common", "Restricted"] as const;
export type HolidayKind = (typeof HOLIDAY_KINDS)[number];

export const ATTENDANCE_STATUSES = ["present", "leave", "lop", "holiday", "weekend"] as const;
export type AttendanceStatus = (typeof ATTENDANCE_STATUSES)[number];

/** Where an `attendance_days` row came from — the column's CHECK, in one place. */
export const ATTENDANCE_SOURCES = ["system", "seed", "import", "manual"] as const;
export type AttendanceSource = (typeof ATTENDANCE_SOURCES)[number];

/**
 * Claim states. Wire values are snake_case; CLAIM_STATUS_LABEL renders the
 * prototype text.
 *
 * `withdrawn` is not in the PRD's machine and is deliberately separate from
 * `rejected`. A claimant taking back their own claim and a manager refusing it
 * are different events with different actors, and recording the first as the
 * second would put a refusal on somebody's record that nobody ever made — as well
 * as tripping the `claims_rejection_note` constraint, which requires the note a
 * withdrawal has no author for.
 */
export const CLAIM_STATES = ["submitted", "manager_approved", "paid", "rejected", "withdrawn"] as const;
export type ClaimState = (typeof CLAIM_STATES)[number];

export const CLAIM_STATUS_LABEL: Record<ClaimState, string> = {
  submitted: "Submitted",
  manager_approved: "Manager approved",
  paid: "Paid",
  rejected: "Rejected",
  withdrawn: "Withdrawn",
};

/**
 * The reimbursement state machine, verbatim from the PRD.
 *
 * `withdrawn` has no edge into it here on purpose. It is not an approver's
 * decision, so it does not travel through `POST /claims/:id/transition` — which
 * refuses every self-decision before it looks at anything else. It has its own
 * endpoint with its own rule: the claimant, on their own still-submitted claim.
 */
export const CLAIM_TRANSITIONS: Record<ClaimState, readonly ClaimState[]> = {
  submitted: ["manager_approved", "rejected"],
  manager_approved: ["paid"],
  paid: [],
  rejected: [],
  withdrawn: [],
};

export const canTransitionClaim = (from: ClaimState, to: ClaimState): boolean =>
  CLAIM_TRANSITIONS[from].includes(to);

export const REIMB_CATEGORIES = [
  "Travel", "Accommodation", "Meals", "Software & tools",
  "Internet", "Client entertainment", "Training", "Other",
] as const;

/**
 * The quota, stated in words, derived from the quota itself.
 *
 * `fte_label` used to be free text sitting beside the number, and the two were
 * under no obligation to agree — the table said "20 days / year" for two years
 * while the 20 that reached anybody's balance was a literal in a switch
 * somewhere else. A label nobody may type is a label that cannot lie, so this is
 * the only way one is produced.
 *
 * Zero days is not "0 days / year": it means the type is not allocated at all,
 * and which of the two things that means depends on whether it is paid. An
 * unpaid type with no allowance is granted when it is approved; a paid one with
 * no allowance simply does not apply to that population — which is what
 * "Marriage Leave, intern" is.
 *
 * A pro-rated entitlement that divides evenly into the year is stated as the
 * monthly accrual people actually experience ("1 day / month (accrues)") rather
 * than as an annual figure nobody ever holds all of at once.
 */
export function leaveQuotaLabel(days: number, proRata: boolean, paid: boolean): string {
  if (days <= 0) return paid ? "Not applicable" : "As approved";
  const unit = (n: number): string => (n === 1 ? "day" : "days");
  if (proRata && days % 12 === 0) {
    const perMonth = days / 12;
    return `${perMonth} ${unit(perMonth)} / month (accrues)`;
  }
  return proRata ? `${days} ${unit(days)} / year (pro-rated)` : `${days} ${unit(days)} / year`;
}

export const REVIEW_STATES = ["Overdue", "Due soon", "Upcoming"] as const;
export type ReviewState = (typeof REVIEW_STATES)[number];

export const REVIEW_KINDS = [
  "Monthly intern review", "Monthly probation review", "Quarterly review",
  "3-month confirmation meeting", "Spontaneous note",
] as const;

/**
 * How often each population is reviewed, in months.
 *
 * The three intervals are configuration — Settings → Configuration → Reviews
 * writes them to `org_settings` and the nightly cadence job reads them — and
 * these are the values a database that has never been configured falls back to,
 * the ones this company started with: interns and probationers monthly,
 * everyone else quarterly.
 *
 * The `kind` label a generated review carries is deliberately *not* derived from
 * the interval. `(employee_id, kind, due_date)` is the natural key of a review
 * instance and the join a logged note is found by, so a label that moved with the
 * interval would orphan every note already written the first time HR changed the
 * cadence. The interval decides when the next review falls due; the label stays
 * the name of the cycle it belongs to.
 */
export const DEFAULT_REVIEW_CADENCE = {
  internMonths: 1,
  probationMonths: 1,
  fullTimeMonths: 3,
} as const;

/** The intervals HR may choose between — a review every N months. */
export const REVIEW_CADENCE_MONTHS = [1, 2, 3, 6, 12] as const;

/**
 * "every month" / "every 2 months" / "every quarter".
 *
 * One phrasing, used by the server when it expands `{{intern_cadence}}` into the
 * Configuration card and by the picker that sets it, so the row a person reads
 * and the option they chose are worded the same.
 */
export function cadencePhrase(months: number): string {
  if (months === 1) return "every month";
  if (months === 3) return "every quarter";
  if (months === 12) return "every year";
  return `every ${months} months`;
}

/** Audit actions that may be undone (PRD: promotion, exit, leave decision). */
export const REVERSIBLE_ACTIONS = ["employee.designation", "employee.exit", "leave.decision"] as const;
export type ReversibleAction = (typeof REVERSIBLE_ACTIONS)[number];

export const RANGE_PRESETS = ["This month", "Last month", "Quarter", "YTD"] as const;
export type RangePreset = (typeof RANGE_PRESETS)[number];

export const TOOLS_URL = "https://tools.botpresso.com";

/**
 * Each tool carries the address it actually opens at.
 *
 * Every card used to link at `TOOLS_URL`, so a new joiner told to "explore the
 * stack in week 1" landed on the same dashboard eight times and had to find the
 * tool again by name. The URLs are the ones tools.botpresso.com launches — its
 * Launch buttons append a short-lived SSO token, which is deliberately not stored
 * here: the origin is the durable part, and each tool signs the visitor in itself.
 *
 * `CloakVigil` is gone from this list because no such tool is on the dashboard;
 * pointing a new joiner at a card they cannot open is worse than not listing it.
 */
export const BOTPRESSO_TOOLS = [
  { name: "GSCVigil", desc: "Search Console insight engine — decay, cannibalization, CTR benchmarks, forecasts", tag: "Search", url: "https://gscvigil.botpresso.com/" },
  { name: "Rank Tracker", desc: "Daily keyword tracking across mobile and desktop SERPs", tag: "Search", url: "https://ranktracker.botpresso.com/" },
  { name: "AIVigil", desc: "Citation tracking across ChatGPT, Perplexity, Gemini and Copilot", tag: "AI", url: "https://aivigil.botpresso.com/projects" },
  { name: "IndexVigil", desc: "URL-level indexing and coverage monitoring", tag: "Search", url: "https://indexvigil.botpresso.com/" },
  { name: "RedditVigil", desc: "Brand and competitor intelligence from Reddit conversations", tag: "Brand", url: "https://redditvigil.botpresso.com/" },
  { name: "WebMonitor", desc: "Site change detection and uptime watch", tag: "Ops", url: "https://webmonitor.botpresso.com/" },
  { name: "Keyword Clustering", desc: "Cluster and analyse keywords for smarter SEO grouping", tag: "Search", url: "https://keywordclustering.botpresso.com/" },
] as const;

/** Prototype SENSITIVE_FIELDS — editing any of these notifies HR and the manager. */
export const SENSITIVE_FIELDS = ["address", "emergency_name", "emergency_relation", "emergency_phone", "bank"] as const;

/**
 * The eight ABO/Rh groups, and the six relationships an emergency contact may
 * hold.
 *
 * Both are closed sets in every HR product this one is modelled on, and both are
 * here rather than in a component because the self-service form and the server's
 * validation have to offer and accept exactly the same values. A blood group is
 * read off a record by a hospital; "ZZ+++" in that field is worse than an empty
 * one, because an empty one is visibly missing.
 */
export const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"] as const;
export type BloodGroup = (typeof BLOOD_GROUPS)[number];

export const EMERGENCY_RELATIONSHIPS = ["Spouse", "Parent", "Sibling", "Child", "Friend", "Other"] as const;
export type EmergencyRelationship = (typeof EMERGENCY_RELATIONSHIPS)[number];

/**
 * A contact number this company can actually ring.
 *
 * Two forms are accepted and nothing else: an Indian mobile — ten digits starting
 * 6–9, optionally prefixed `+91`/`91`/`0` — and full E.164 for the teammates and
 * next-of-kin who are not in India. Spaces, hyphens and brackets are tolerated in
 * the input because people paste numbers out of address books; they are stripped
 * before the pattern is applied, and the stored value is whatever the person
 * typed. What is refused is the thing the field used to accept: free text.
 */
const PHONE_PUNCTUATION = /[\s()\-.]/g;
const INDIAN_MOBILE = /^(?:\+?91|0)?[6-9]\d{9}$/;
const E164 = /^\+[1-9]\d{7,14}$/;

export function isContactNumber(raw: string): boolean {
  const compact = raw.replace(PHONE_PUNCTUATION, "");
  return INDIAN_MOBILE.test(compact) || E164.test(compact);
}

export const MAX_UPLOAD_BYTES_DEFAULT = 10 * 1024 * 1024;
export const ALLOWED_UPLOAD_MIME = ["application/pdf", "image/jpeg", "image/png"] as const;

/**
 * A profile photo is a picture, and a small one.
 *
 * PDF is off the list because a document is not a face — the vault takes those —
 * and because the photo endpoint serves its bytes inline into an `<img>`, where a
 * PDF renders as a broken image rather than as an error anybody can act on.
 *
 * The size cap is half the document cap: large enough for a photo taken on a
 * phone and handed over untouched, small enough that a screen drawing sixty
 * avatars is not moving half a gigabyte. Nothing is re-encoded server-side — see
 * D24 — so this number is also the largest image any one avatar will ever pull.
 * The browser is told the same number so an oversized file is refused before it
 * is uploaded rather than after.
 */
export const ALLOWED_PHOTO_MIME = ["image/jpeg", "image/png"] as const;
export const MAX_PHOTO_BYTES = 5 * 1024 * 1024;

/* ------------------------------------------------------------------ *
 * Pure date + leave helpers. Ported from the prototype so client-side
 * display and server-side enforcement cannot disagree about the maths.
 * ------------------------------------------------------------------ */

export const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
] as const;

export const DOW_LETTERS = ["S", "M", "T", "W", "T", "F", "S"] as const;
export const DOW_FULL = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

/** `2026-07-22` → `22 Jul 2026`. Prototype `fmtDate`. */
export function fmtDate(d: string | null | undefined): string {
  if (!d) return "—";
  const [y, m, dd] = d.split("-");
  return `${dd} ${MONTHS[Number(m) - 1].slice(0, 3)} ${y}`;
}

export function isoDate(y: number, monthIndex: number, day: number): string {
  return `${y}-${String(monthIndex + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/**
 * A `YYYY-MM-DD` string that names a day which actually exists.
 *
 * The shape regex the API validates dates with accepts `2026-02-30` and
 * `2026-13-01` quite happily; Postgres then answers `date/time field value out of
 * range`, which is a database error printed on a form field. The round trip
 * through UTC is the check: `Date` rolls an impossible day forward, so a value
 * that survives unchanged is a real calendar date.
 */
export function isCalendarDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

/** Calendar-day difference. Both arguments are `YYYY-MM-DD`. */
export function daysBetween(a: string, b: string): number {
  return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);
}

export function isWeekend(dateStr: string): boolean {
  const dow = new Date(`${dateStr}T00:00:00Z`).getUTCDay();
  return dow === 0 || dow === 6;
}

export function dayOfWeek(dateStr: string): number {
  return new Date(`${dateStr}T00:00:00Z`).getUTCDay();
}

export function addDays(dateStr: string, n: number): string {
  const d = new Date(`${dateStr}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** Prototype `addMonths`, clamped so 31 Jan + 1 month never rolls into March. */
export function addMonths(dateStr: string, n: number): string {
  const d = new Date(`${dateStr}T00:00:00Z`);
  const day = d.getUTCDate();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + n);
  const lastDay = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  d.setUTCDate(Math.min(day, lastDay));
  return d.toISOString().slice(0, 10);
}

export function daysInMonth(year: number, monthIndex: number): number {
  return new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
}

/**
 * Inclusive working days between two dates, excluding weekends and the supplied
 * holiday dates. A single-day half-day request is 0.5. Prototype `workingDays`.
 */
export function workingDays(from: string, to: string, holidays: ReadonlySet<string>): number {
  if (!from || !to || to < from) return 0;
  let n = 0;
  for (let d = from; d <= to; d = addDays(d, 1)) {
    if (!isWeekend(d) && !holidays.has(d)) n++;
  }
  return n;
}

/**
 * Leave duration in days for a request, applying the half-day rule.
 * Half days only exist on single-day requests (prototype ApplyLeave).
 */
export function leaveDays(
  from: string,
  to: string,
  duration: LeaveDuration,
  holidays: ReadonlySet<string>,
): number {
  if (from === to && duration !== "Full day") return 0.5;
  return workingDays(from, to, holidays);
}

/**
 * Sick leave beyond two working days requires a medical certificate.
 *
 * The business-behaviour contract ("Sick > 2 days ⇒ certificate required") outranks
 * the prototype's `days > 3` expression under the documented precedence order.
 */
export const SICK_CERT_THRESHOLD_DAYS = 2;

export function sickCertificateRequired(leaveTypeName: string, days: number): boolean {
  return leaveTypeName === "Sick Leave" && days > SICK_CERT_THRESHOLD_DAYS;
}

/** Prototype `monthsWorkedIn2026` — intern casual-leave accrual basis. */
export function monthsWorkedInYear(doj: string | null, year = 2026): number {
  if (!doj) return 12;
  const d = new Date(`${doj}T00:00:00Z`);
  if (d.getUTCFullYear() < year) return 12;
  if (d.getUTCFullYear() > year) return 0;
  const startMonth = d.getUTCDate() <= 16 ? d.getUTCMonth() : d.getUTCMonth() + 1;
  return Math.max(0, 12 - startMonth);
}

export function tenureYears(doj: string, asOf: string): number {
  return daysBetween(doj, asOf) / 365.25;
}

/**
 * `₹1,450` — Indian lakh/crore grouping, and exact to the paise a value carries.
 *
 * The prototype's version was `Math.round(...)`, which was harmless only while
 * every amount in the product was a whole rupee. `claim_lines.amount` and
 * `claims.total_amount` are `numeric(12,2)` and the API's `zMoney` accepts two
 * decimals, so a claim with paise in it had each line rounded and its total
 * rounded separately — and the lines stopped adding up to the figure Finance
 * pays. Rupee amounts still print without a decimal point, because printing
 * "₹1,450.00" everywhere to accommodate the rare paise would be its own noise.
 */
export function inr(n: number | string): string {
  const amount = Number(n);
  if (!Number.isFinite(amount)) return "₹0";
  const hasPaise = Math.round(amount * 100) % 100 !== 0;
  return "₹" + amount.toLocaleString("en-IN", {
    minimumFractionDigits: hasPaise ? 2 : 0,
    maximumFractionDigits: 2,
  });
}

/** Prototype `hashStr` (FNV-1a) — drives the deterministic identicon. */
export function hashStr(str: string): number {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Prototype claim attachment naming convention. */
export function claimFileName(empName: string, claimMonth: string, submittedOn: string): string {
  return `${submittedOn.replace(/-/g, "")} ${claimMonth} ${empName} Reimbursement`;
}
